import React from 'react'
import { OpleidingenOverviewPageState, OpleidingFilter } from './opleidingenOverviewState'
import { SearchForm } from '../search/searchBlock'
import { AsyncState, Link, navigate } from 'widgets-for-react'
import i18next from 'i18next'
import { PaginationLayout } from '../../pagination/paginationLayout'
import { Image } from '../../utils/images'
import { useInView } from '../../utils/hooks/hooks/useInView'
import { DataLayer } from '../../utils/datalayer'
import { ResourceInfo } from '../cmsPage/sharepointPageApi/types/pageData'
import { OverviewCourseItem } from './opleidingenOverviewState'

export type OpleidingenOverviewLayoutProps = {
  state: OpleidingenOverviewPageState
  setIndividualFilter: (filterName: string, isSelected: boolean) => void
  setQuery: (newQuery: string) => void
  setOpenMobileSidebar: (isOpen: boolean) => void
}

export const OpleidingenOverviewLayout = (props: OpleidingenOverviewLayoutProps) => {
  return (
    <div className="opleidingen-overview container--m gutter--m">
      <div className="opleidingen-overview__inner">
        <FilterSideMenu
          openMobileSidebar={props.state.openMobileSidebar}
          query={props.state.query ?? ''}
          filters={props.state.filters}
          setIndividualFilter={props.setIndividualFilter}
          setQuery={props.setQuery}
          setOpenMobileSidebar={props.setOpenMobileSidebar}
        />
        <CourseList state={props.state} />
      </div>
      <OpleidingenPagination state={props.state} />
    </div>
  )
}

export const FilterSideMenu = (p: {
  filters: AsyncState<OpleidingFilter[]>
  query: string
  openMobileSidebar: boolean
  setIndividualFilter: (filterName: string, isSelected: boolean) => void
  setQuery: (newQuery: string) => void
  setOpenMobileSidebar: (isOpen: boolean) => void
}) => {
  const onSearchSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    const newFiltersUrlParam =
      p.filters.kind === 'loaded'
        ? p.filters.value
            .filter((filter) => filter.isSelected)
            .map((filter) => filter.name)
            .join(',')
        : ''
    DataLayer.CourseOverviewSearchEvent(p.query, newFiltersUrlParam, 0);
    navigateToNewResult(p.query, newFiltersUrlParam)
  }

  return (
    <aside className="opleidingen-filters filter">
      <h3 className="filter__title title--s">{i18next.t('opleidingen:filterTitle')}</h3>
      <div className="filter__inner">
        <div className="filters--search">
          <SearchForm
            onFormSubmit={onSearchSubmit}
            query={p.query}
            setQuery={p.setQuery}
            disabled={false}
            showButtonLabel={false}
            placeholder={i18next.t('opleidingen:searchInputPlaceholder')}
          />
        </div>
        <div className="filters--divider" />
        {!p.openMobileSidebar && <FilterCategories {...p} display="desktop" />}
        <button
          className="filter__button button button--primary button--icon display-mobile"
          onClick={() => p.setOpenMobileSidebar(true)}
        >
          <span className="button__label">{'Filter resultaten'}</span>
          <svg className="button__icon" width="24px" height="24px">
            <use xlinkHref="#icon--filter" />
          </svg>
        </button>
      </div>
      <MobileFilterSideBar {...p} />
    </aside>
  )
}

export const FilterCategories = (p: {
  filters: AsyncState<OpleidingFilter[]>
  setIndividualFilter: (filterName: string, isSelected: boolean) => void
  query: string
  display: 'mobile' | 'desktop'
}) => {
  const onCheckboxChange = (filterName: string, isSelected: boolean, shouldNavigate: boolean) => {
    const newFiltersUrlParam =
      p.filters.kind === 'loaded'
        ? p.filters.value
            .map((filter) =>
              filter.name === filterName ? (isSelected ? filter.name : null) : filter.isSelected ? filter.name : null
            )
            .filter((name): name is string => name !== null)
            .join(',')
        : ''

    p.setIndividualFilter(filterName, isSelected)
    shouldNavigate && navigateToNewResult(p.query, newFiltersUrlParam)
  }

  return (
    <ul className={`filters--category filter__content display-${p.display}`}>
      {p.filters.kind === 'loaded' &&
        p.filters.value.map((filter) => (
          <li key={filter.name} className="filter-item checkbox">
            <input
              className="checkbox__input"
              id={`a11y-sg-cb-${filter.name}`}
              type="checkbox"
              checked={filter.isSelected}
              onChange={() => {
                onCheckboxChange(filter.name, !filter.isSelected, p.display === 'desktop')
              }}
            />
            <label
              className="checkbox__label"
              htmlFor={`a11y-sg-cb-${filter.name}`}
            >{`${filter.name} (${filter.availableCourses})`}</label>
          </li>
        ))}
    </ul>
  )
}

export const MobileFilterSideBar = (p: {
  openMobileSidebar: boolean
  setOpenMobileSidebar: (isOpen: boolean) => void
  filters: AsyncState<OpleidingFilter[]>
  query: string
  setIndividualFilter: (filterName: string, isSelected: boolean) => void
  setQuery: (newQuery: string) => void
}) => {
  const filterResults = () => {
    const newFiltersUrlParam =
      p.filters.kind === 'loaded'
        ? p.filters.value
            .filter((filter) => filter.isSelected)
            .map((filter) => filter.name)
            .join(',')
        : ''
    p.setOpenMobileSidebar(false)
    navigateToNewResult(p.query, newFiltersUrlParam)
  }

  return (
    <>
      {p.openMobileSidebar && (
        <div className="filter--mobile-overlay" onClick={() => p.setOpenMobileSidebar(false)}></div>
      )}
      <div className={`filter--mobile-sidebar display-mobile menu--${p.openMobileSidebar ? 'open' : 'closed'}`}>
        <header className="filter--mobile-sidebar__header">
          <h3>{i18next.t('opleidingen:filterTitle')}</h3>
          <button
            className="filter--mobile-sidebar__close button button--icon"
            onClick={() => p.setOpenMobileSidebar(false)}
          >
            <svg className="button__icon" width="24px" height="24px">
              <use xlinkHref="#icon--close" />
            </svg>
            <span className="sr-text">{i18next.t('opleidingen:filterScreenReaderText')}</span>
          </button>
        </header>
        <FilterCategories {...p} display="mobile" />
        <footer>
          <button className="filter__button button button--primary" onClick={() => filterResults()}>
            <span className="button__label">{i18next.t('opleidingen:filterResultsButton')}</span>
          </button>
        </footer>
      </div>
    </>
  )
}

export const CourseList = (p: { state: OpleidingenOverviewPageState }) => {
  return (
    <div className="opleidingen-courses">
      <section className="listing">
        <div className="listing__inner">
          <header className="listing__header">
            <h3 className="listing__title title--s">
              {`${p.state.opleidingen.kind === 'loaded' ? p.state.opleidingen.value.Total : 0} ${i18next.t('opleidingen:resultsFound')}`}
            </h3>
          </header>
          <div className="listing__content">
            <ul className="listing__list courses--list">
              {p.state.opleidingen.kind === 'loaded' &&
                p.state.opleidingen.value.Resources.map((course) => (
                  <CourseListItem key={course.Resource.url} course={course} />
                ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  )
}

export const CourseListItem = (p: { course: ResourceInfo<OverviewCourseItem> }) => {
  const { Resource, Highlight } = p.course
  const { ref } = useInView<HTMLLIElement>({ threshold: 0.1 }, (isVisible) => {
    if (isVisible) DataLayer.CourseOverviewItemEvent('view', Resource.url, Resource.title)
  })

  return (
    <li ref={ref} id={`course-${Resource.url}`} className="listing__item">
      <article className="listingitem">
        <Link
          className="listing__link"
          to={Resource.url}
          onClick={() => DataLayer.CourseOverviewItemEvent('click', Resource.url, Resource.title)}
        >
          <div className="listingitem__visual">
            {Resource.image && (
              <Image
                {...Resource.image}
                classNames={{ image: 'listingitem__image', picture: 'listingitem__picture' }}
              />
            )}
          </div>
          <div>
            <header className="listingitem__header">
              <div className="listingitem__label-wrapper">
                <p className="listingitem__label label--m">{Resource.label}</p>
              </div>
              <h4
                className="listingitem__title title--xs-s highlight-em"
                dangerouslySetInnerHTML={{
                  __html:
                    Highlight.HeaderTitle && Highlight.HeaderTitle.length > 0
                      ? Highlight.HeaderTitle[0]
                      : Resource.title,
                }}
              />
            </header>

            <div className="listingitem__meta text--sm">
              {(Resource.newprice || Resource.oldprice) && (
                <div>
                  {Resource.newprice && (
                    <span className="meta--newprice">{`Ledenprijs: €${Resource.newprice}`}</span>
                  )}
                  {Resource.oldprice && <span className="meta--price">{`Prijs: €${Resource.oldprice}`}</span>}
                </div>
              )}
              {Resource.duration && (
                <div className="meta--duration text--sm">
                  <svg width="20px" height="20px">
                    <use xlinkHref="#icon--clock" />
                  </svg>
                  <span>{Resource.duration}</span>
                </div>
              )}
            </div>
          </div>
        </Link>
      </article>
    </li>
  )
}

export const OpleidingenPagination = (p: { state: OpleidingenOverviewPageState }) => {
  return (
    <>
      {p.state.opleidingen.kind === 'loaded' && (
        <PaginationLayout
          currentPage={p.state.page || 1}
          totalPages={Math.ceil(p.state.opleidingen.value.Total / p.state.opleidingen.value.PerPage)}
          toPage={(page) =>
            `${location.pathname}?${new URLSearchParams({
              search: p.state.query ?? '',
              page: page.toString(),
              filters:
                p.state.filters.kind === 'loaded'
                  ? p.state.filters.value
                      .filter((filter) => filter.isSelected)
                      .map((filter) => filter.name)
                      .join(',')
                  : '',
            }).toString()}`
          }
        />
      )}
    </>
  )
}

const navigateToNewResult = (query: string, filters: string) => {
  navigate(
    `${location.pathname}?${new URLSearchParams({
      search: query,
      page: '1',
      filters: filters,
    }).toString()}`
  )
}
